/* ChannelModal.jsx */

import React, { useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useWorkspace } from 'context/WorkspaceContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './ChannelModal.css';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import RichSelect from 'components/richselect/RichSelect';
import TextInput from 'components/textinput/TextInput';

// Services
import { createChannel } from 'channels/services/ChannelWriteService';
import { CHANNEL_TYPES } from 'channels/constants/channelTypes';

/**
 * ChannelModal
 *
 * Captures a channel title and creates a workspace channel.
 *
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Close callback.
 * @param {string|null} moduleKey - Optional module key for module-scoped channels.
 *
 * @returns {ReactNode} Rendered channel modal.
 */
const ChannelModal = React.memo(({ isOpen, onClose, moduleKey = null }) => {
    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();

    // User Context
    const { user } = useUser();

    // Workspace Context
    const { activeWorkspace } = useWorkspace();

    // Routing
    const navigate = useNavigate();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- STATE ----- //

    // Form State
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [channelType, setChannelType] = useState(CHANNEL_TYPES.TEXT);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ----- MEMOIZED VALUES ----- //

    // Determine if the form can be submitted based on required fields and submission state
    const canSubmit = useMemo(() => {
        return Boolean(title.trim() && activeWorkspace?.key && user?.key && !isSubmitting);
    }, [title, activeWorkspace?.key, user?.key, isSubmitting]);

    const channelTypeOptions = useMemo(() => {
        return [
            {
                value: CHANNEL_TYPES.TEXT,
                label: 'Text Chat',
                iconKey: 'tag',
                note: 'Asynchronous conversations with searchable message history.',
            },
            {
                value: CHANNEL_TYPES.VOICE_VIDEO,
                label: 'Voice / Video',
                iconKey: 'video_camera_front',
                note: 'Live room for calls and collaboration with channel updates.',
            },
        ];
    }, []);

    /**
     * handleClose
     * 
     * Handles closing the modal, preventing close if a submission is in progress.
     */
    const handleClose = () => {
        if (isSubmitting) return;
        onClose && onClose();
    };

    /**
     * handleCreateChannel
     * 
     * Handles creating a new channel using the createChannel service, then navigates to the new channel.
     */
    const handleCreateChannel = async () => {
        if (!canSubmit) return;

        setIsSubmitting(true);
        try {
            const created = await createChannel({
                workspaceKey: activeWorkspace.key,
                userKey: user.key,
                userName: user.name || user.title || user.displayName || user.email || 'Workspace Member',
                title,
                moduleKey,
                topic: description,
                channelType,
            });

            const channelKey = created?.key;
            if (channelKey) {
                handleClose();
                navigate(`/channels/${channelKey}`);
                setTitle('');
                setDescription('');
                setChannelType(CHANNEL_TYPES.TEXT);
            }
        } catch (error) {
            console.error('[ChannelModal] create channel error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ----- RENDER ----- //

    return (

        <Modal
            title="Channel"
            isOpen={isOpen}
            onClose={handleClose}
            width={isMobile ? '100%' : 460}
            height="auto"
        >
            <ModalForm>

                {/* NAME */}
                <ModalField>

                    <ModalLabel
                        label="Name"
                    />

                    <TextInput
                        value={title}
                        placeholder="Channel name"
                        onChange={(event) => setTitle(event.target.value)}
                        autoFocus
                    />

                    <ModalHint
                        hint="The display name of the channel."
                    />

                </ModalField>

                {/* CHANNEL TYPE */}
                <ModalField>

                    <ModalLabel
                        label="Type"
                    />

                    <RichSelect
                        value={channelType}
                        options={channelTypeOptions}
                        placeholder="Select a channel type"
                        onValueChange={(value) => setChannelType(value)}
                        style={{
                            backgroundColor: theme.input,
                            borderColor: theme.backgroundMuted,
                            color: theme.foreground,
                            width: '100%',
                        }}
                    />

                    <ModalHint
                        hint="The type of channel to create: Text Chat or Voice / Video."
                    />

                </ModalField>

                {/* DESCRIPTION */}
                <ModalField>

                    <ModalLabel
                        label="Description"
                    />

                    <TextInput
                        value={description}
                        placeholder="What is this channel for?"
                        multiline
                        expandable
                        showCount
                        maxLength={280}
                        onChange={(event) => setDescription(event.target.value)}
                    />

                    <ModalHint
                        hint="Optional description to provide more information and context about the purpose of the channel."
                    />

                </ModalField>

            </ModalForm>

            {/* FOOTER */}
            <ModalButtons>
                <Button
                    iconKey="check"
                    label={isSubmitting ? 'Creating...' : 'Create Channel'}
                    enabled={canSubmit}
                    onClick={handleCreateChannel}
                    height="44px"
                />
            </ModalButtons>

        </Modal>
    );
});

ChannelModal.displayName = 'ChannelModal';

export default ChannelModal;
